import {NewsletterSection_Content} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  ContentPopper,
  ContentPopperProps,
  SecondaryContentPopperButton,
} from '@aboutyou/ui-components';
import React from 'react';
import {useNewsletterFlyoutStateManager} from '../hooks/useNewsletterFlyoutStateManager';
import {useNewsletterFormHandler} from '../hooks/useNewsletterFormHandler';
import {useNewsletterTracking} from '../hooks/useNewsletterTracking';
import {FormNotificationMessage} from './FormNotificationMessage';
import {NewsletterForm} from './NewsletterForm';

const NEWSLETTER_FORM_ID = 'newsletterForm';

export interface NewsletterFormOpenerProps {
  newsletterUserProcessState: NewsletterSection_Content['type'];
  isCreativeCampaign: boolean;
  LayoutComponent: ContentPopperProps['LayoutComponent'];
  className?: string;
}

export const NewsletterFormOpener: React.FC<NewsletterFormOpenerProps> = ({
  newsletterUserProcessState,
  isCreativeCampaign,
  LayoutComponent,
  className,
}) => {
  const {t} = useTranslation();
  const {handleTrackComponent} = useNewsletterTracking();
  const {isOpen, cancel, open} = useNewsletterFlyoutStateManager();

  const isNotSubscribed = newsletterUserProcessState?.$case === 'notSubscribed';

  const newsletterFormHandler = useNewsletterFormHandler({
    showErrorMessage: true,
    newlySubscribedConfirmMessage: isNotSubscribed
      ? newsletterUserProcessState?.notSubscribed.confirmText
      : '',
  });

  const handleOpen = () => {
    open();
  };

  const handleClose = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.preventDefault();
    e?.stopPropagation();
    cancel();
  };

  const showForm =
    isNotSubscribed && !newsletterFormHandler.newsletterSubscriptionResponse;

  const ctaLabel = isNotSubscribed
    ? newsletterUserProcessState.notSubscribed.ctaLabel
    : '';

  return (
    <>
      {showForm && (
        <>
          <Button
            variant={isCreativeCampaign ? 'primary' : 'secondary'}
            onClick={handleOpen}
            data-testid="openFlyoutButton"
            className={className}
          >
            {ctaLabel}
          </Button>
          <ContentPopper
            LayoutComponent={LayoutComponent}
            renderInPortal
            open={isOpen}
            onClose={handleClose}
            headerContent={t('Newsletter Sign up')}
            bodyContent={
              <NewsletterForm
                newsletterFormHandler={newsletterFormHandler}
                isUserNotSubscribed={isNotSubscribed}
                formId={NEWSLETTER_FORM_ID}
              />
            }
            footerContent={
              <>
                <SecondaryContentPopperButton
                  label={t('Cancel')}
                  onClick={handleClose}
                />
                <Button
                  data-testid="subscribeButton"
                  htmlType="submit"
                  form={NEWSLETTER_FORM_ID}
                  isLoading={newsletterFormHandler.isSubmitting}
                  onClick={() =>
                    handleTrackComponent({
                      action: 'submit',
                      gender: newsletterFormHandler.getValues('gender'),
                    })
                  }
                >
                  {t('Notify me')}
                </Button>
              </>
            }
          />
        </>
      )}

      <FormNotificationMessage
        newsletterUserProcessState={newsletterUserProcessState}
        newsletterSubscriptionResponse={
          newsletterFormHandler.newsletterSubscriptionResponse
        }
      />
    </>
  );
};
